// Memory optimization utilities for WebSocket data processing

// String interning pool to prevent duplicate string allocations
const stringPool = new Map();
const MAX_POOL_SIZE = 10000;

// Intern strings to reduce memory usage
export const internString = (str) => {
  if (typeof str !== 'string') return str;
  
  // Don't intern very long strings
  if (str.length > 100) return str;
  
  if (stringPool.has(str)) {
    return stringPool.get(str);
  }
  
  // Prevent unbounded growth
  if (stringPool.size >= MAX_POOL_SIZE) {
    // Clear least recently used entries (simple FIFO for now)
    const firstKey = stringPool.keys().next().value;
    stringPool.delete(firstKey);
  }
  
  stringPool.set(str, str);
  return str;
};

// Optimized JSON parser with string interning
export const parseWithInterning = (jsonString) => {
  return JSON.parse(jsonString, (key, value) => {
    if (typeof value === 'string' && value.length < 100) {
      return internString(value);
    }
    return value;
  });
};

// Map for caching parsed results
const stringToObjectCache = new Map();
const MAX_CACHE_SIZE = 100;

// Memoized JSON parser with caching
export const memoizedParse = (jsonString) => {
  // For small messages, use direct caching
  if (jsonString.length < 1000) {
    if (stringToObjectCache.has(jsonString)) {
      return stringToObjectCache.get(jsonString);
    }
    
    const parsed = parseWithInterning(jsonString);
    
    // Prevent cache from growing too large
    if (stringToObjectCache.size >= MAX_CACHE_SIZE) {
      const firstKey = stringToObjectCache.keys().next().value;
      stringToObjectCache.delete(firstKey);
    }
    
    stringToObjectCache.set(jsonString, parsed);
    return parsed;
  }
  
  // For larger messages, just parse with interning
  return parseWithInterning(jsonString);
};

// Optimized array update that minimizes allocations
export const updateRecentBlocksOptimized = (currentBlocks, newBlock) => {
  // Check if block already exists
  const existingIndex = currentBlocks.findIndex(block => block.height === newBlock.height);
  if (existingIndex !== -1) {
    return currentBlocks; // No update needed
  }
  
  // Instead of creating new array, modify in place when possible
  const maxBlocks = 10; // Assuming we keep 10 recent blocks
  
  if (currentBlocks.length < maxBlocks) {
    return [...currentBlocks, newBlock];
  }
  
  // Reuse array by shifting elements
  const newBlocks = currentBlocks.slice(1);
  newBlocks.push(newBlock);
  return newBlocks;
};

// Specialized intern for pool names which are frequently repeated
const poolNameCache = new Map();
const COMMON_POOL_NAMES = [
  'Foundry USA', 'AntPool', 'F2Pool', 'ViaBTC', 'Binance Pool',
  'Braiins Pool', 'BTC.com', 'Poolin', 'MARA Pool', 'Luxor',
  'BTC.TOP', 'SlushPool', 'Unknown', 'SBI Crypto', 'Bitfury'
];

// Pre-intern common pool names
COMMON_POOL_NAMES.forEach(name => {
  poolNameCache.set(name, name);
});

export const internPoolName = (poolName) => {
  if (!poolName || typeof poolName !== 'string') return poolName;
  
  if (poolNameCache.has(poolName)) {
    return poolNameCache.get(poolName);
  }
  
  // Only cache pool names to prevent unbounded growth
  if (poolNameCache.size < 100) {
    poolNameCache.set(poolName, poolName);
  }
  
  return poolName;
};

// Enhanced JSON parser with pool name interning
export const parseBlockData = (jsonString) => {
  const parsed = memoizedParse(jsonString);
  
  // Intern pool names in block data
  if (parsed && parsed.extras && parsed.extras.pool && parsed.extras.pool.name) {
    parsed.extras.pool.name = internPoolName(parsed.extras.pool.name);
  }
  
  // Handle arrays of blocks
  if (Array.isArray(parsed)) {
    parsed.forEach(block => {
      if (block && block.extras && block.extras.pool && block.extras.pool.name) {
        block.extras.pool.name = internPoolName(block.extras.pool.name);
      }
    });
  }
  
  return parsed;
};

// Clear caches periodically to prevent unbounded growth
let clearTimer = null;
export const scheduleCacheClear = () => {
  if (clearTimer) return;
  
  clearTimer = setTimeout(() => {
    // Keep only frequently used strings
    if (stringPool.size > MAX_POOL_SIZE / 2) {
      stringPool.clear();
    }
    if (stringToObjectCache.size > MAX_CACHE_SIZE / 2) {
      stringToObjectCache.clear();
    }
    clearTimer = null;
  }, 300000); // Clear every 5 minutes
};